import { appHref } from './globals.js';

export interface ApiFetchOptions {
  method?: string;
  body?: unknown;
  token?: string;
  /**
   * Let the request outlive the page. Only for the small writes made on the
   * way out (`pagehide`) — without it the browser cancels them, which is
   * exactly the moment a "where was I" note needs to survive.
   */
  keepalive?: boolean;
}

export async function apiFetch(path: string, opts: ApiFetchOptions = {}): Promise<Response> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (opts.token) headers['authorization'] = `Bearer ${opts.token}`;
  const init: RequestInit = {
    method: opts.method ?? (opts.body !== undefined ? 'POST' : 'GET'),
    headers,
  };
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body);
  if (opts.keepalive) init.keepalive = true;
  return fetch(appHref(path), init);
}


/**
 * What to say when the request never reached the server at all.
 *
 * `apiFetch` rejects — rather than resolving with a failure status — when the
 * browser is offline, the box is down, or DNS fails, so every caller that
 * guards with `.catch(() => undefined)` has an `undefined` response to
 * explain. Naming the connection matters: the alternative is a message about
 * the operation ("Could not save the new order") that sends someone looking
 * for a fault in their data.
 */
const UNREACHABLE = 'Could not reach the server — check your connection.';

/**
 * Why a request failed, in the server's own words where it gave any.
 *
 * Read the body through this rather than inline: `res?.json().catch(...)`
 * looks safe and is not, because optional chaining short-circuits the *whole*
 * chain — including the `.catch()` — the moment `res` is `undefined`, leaving
 * `undefined` where the fallback `{}` was meant to be and throwing a
 * `TypeError` on the next property read. That throw lands inside a
 * fire-and-forget `void (async () => ...)()`, so the handler dies silently
 * partway through: no message, and any button left saying "Saving…" stays
 * that way.
 */
export async function failureMessage(res: Response | undefined, fallback: string): Promise<string> {
  if (!res) return UNREACHABLE;
  const body = (await res.json().catch(() => ({}))) as { message?: string };
  return body.message ?? fallback;
}

/**
 * As `failureMessage`, for the endpoints that answer with a machine `error`
 * code and no sentence for a person. The code is appended rather than dropped
 * because these are the conflict and lease refusals, where "could not save"
 * on its own leaves nobody — user or support — able to say what happened.
 *
 * `fallback` is given without a trailing full stop; this adds one.
 */
export async function failureDetail(res: Response | undefined, fallback: string): Promise<string> {
  if (!res) return UNREACHABLE;
  const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
  // A sentence from the server wins when there is one — a refusing hook's
  // own words (`detail` in core-server) say more than the code does.
  if (body.message) return `${fallback}: ${body.message}`;
  return body.error ? `${fallback}: ${body.error}.` : `${fallback}.`;
}
